import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  StackProps,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useBeforeUnload } from "../../lib/useBeforeUnload";
import useCustomToast from "../../lib/useCustomToast";
import { useHaveEmail } from "../../lib/useHaveEmail";
import { ButtonPrimary } from "../button/ButtonPrimary";
import { PasswordInput } from "../input/PasswordInput";
import { requestFCMToken } from "../../services/firebase.config";

interface Props extends StackProps {
  url: string;
}

const initialValues = {
  password: undefined,
  confirmationPassword: undefined,
};

export const RegisterPasswordForm = ({ url, ...props }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();
  const nativeToast = useToast();
  const navigate = useNavigate();
  useHaveEmail();
  useBeforeUnload();

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      password: yup
        .string()
        .required("Password harus diisi")
        .min(8, "Password harus memiliki minimal 8 karakter")
        .max(32, "Password tidak boleh lebih dari 32 karakter")
        .matches(/[a-z]/, "Password harus mengandung huruf kecil")
        .matches(/[A-Z]/, "Password harus mengandung huruf besar")
        .matches(/[0-9]/, "Password harus mengandung angka"),
      confirmationPassword: yup
        .string()
        .required("Konfirmasi password harus diisi")
        .min(8, "Password harus memiliki minimal 8 karakter")
        .max(32, "Password tidak boleh lebih dari 32 karakter")
        .matches(/[a-z]/, "Password harus mengandung huruf kecil")
        .matches(/[A-Z]/, "Password harus mengandung huruf besar")
        .matches(/[0-9]/, "Password harus mengandung angka")
        .oneOf([yup.ref("password")], "Password harus sama"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const registeredEmail = localStorage.getItem("registeredEmail");

      const vapidKey = `${process.env.REACT_APP_VAPID_KEY}`;
      let fcmToken: string | null = "";

      if (Notification.permission === "granted") {
        fcmToken = await requestFCMToken(vapidKey);
      }

      const request = {
        email: registeredEmail,
        password: values.password,
        password_confirmation: values.confirmationPassword,
        fcm_token: fcmToken,
      };

      axios
        .post(
          `${process.env.REACT_APP_WEB_API_BASE_URL}/v1/auth/buyer/add-password`,
          request
        )
        .then((res) => {
          const redirect = new Promise((resolve, reject) => {
            setTimeout(() => {
              resolve(5000);
              navigate("/login");
              localStorage.removeItem("registeredEmail");
            }, 5500);
          });

          nativeToast.promise(redirect, {
            success: {
              title: "Mengarahkan sekarang",
              description:
                "Anda bisa login menggunakan password anda yang baru",
            },
            error: {
              title: "Gagal mengarahkan",
              description: "Refresh halaman untuk tetap terhubung",
            },
            loading: {
              title: res.data.message,
              description:
                "Tunggu beberapa saat lagi, Kami akan mengarahkan anda ke halaman login...",
            },
          });
        })
        .catch((err) => {
          toast({
            title: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <VStack p={10} {...props}>
      <Text
        fontWeight={"semibold"}
        fontSize={[18, null, 20]}
        textAlign={"center"}
      >
        Atur password untuk segala akun kamu
      </Text>

      <Text
        variant={"secondary"}
        textAlign={"center"}
        fontSize={[12, null, 14]}
      >
        Satu langkah lagi untuk bisa mengakses akun kamu nih
      </Text>

      <form
        id="registerPasswordForm"
        onSubmit={formik.handleSubmit}
        style={{ width: "100%", marginTop: "40px" }}
      >
        <VStack w={"100%"} gap={6}>
          <FormControl
            isInvalid={
              formik.errors.password && formik.touched.password ? true : false
            }
          >
            <FormLabel htmlFor="password">Password</FormLabel>
            <PasswordInput
              onConfirm={(inputValue) => {
                formik.setFieldValue("password", inputValue);
              }}
              placeholder="********"
            />
            <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={
              formik.errors.confirmationPassword &&
              formik.touched.confirmationPassword
                ? true
                : false
            }
          >
            <FormLabel htmlFor="confirmationPassword">
              Konfirmasi Password
            </FormLabel>
            <PasswordInput
              onConfirm={(inputValue) => {
                formik.setFieldValue("confirmationPassword", inputValue);
              }}
              placeholder="********"
            />
            <FormErrorMessage>
              {formik.errors.confirmationPassword}
            </FormErrorMessage>
          </FormControl>

          <ButtonPrimary
            form="registerPasswordForm"
            isLoading={loading}
            loadingText="Loading"
            spinnerPlacement="start"
            type="submit"
            w={"100%"}
          >
            Daftar
          </ButtonPrimary>
        </VStack>
      </form>
    </VStack>
  );
};
